import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Training from "./pages/Training";
import Mistakes from "./pages/Mistakes";
import AIMSPack from "./pages/AIMSPack";
import Favorites from "./pages/Favorites";
import Premium from "./pages/Premium";
import Admin from "./pages/Admin";
import Review from "./pages/Review";
import SSM from "./pages/SSM";
import VIP from "./pages/VIP";
import VIPLesson from "./pages/VIPLesson";
import VIPExam, { VIPExamAttempt } from "./pages/VIPExam";
import VIPOralExam from "./pages/VIPOralExam";
import OralCoach from "./pages/OralCoach";
import VerifyCertificate from "./pages/VerifyCertificate";
import VIPAdminSimulator from "./pages/VIPAdminSimulator";
import VIPAdmin, { VIPAdminOverview, VIPAdminCosts, VIPAdminUsers, VIPAdminMigrate, VIPAdminFunnel, VIPAdminVideos, VIPAdminPayments, VIPAdminSecurityReviews, VIPAdminSubscribers, VIPAdminDevices } from "./pages/VIPAdmin";
import VIPAdminContent from "./pages/VIPAdminContent";
import VIPAdminLessonBuilder from "./pages/VIPAdminLessonBuilder";
import VIPAdminUserDetail from "./pages/VIPAdminUserDetail";
import ImpersonationBanner from "./components/ImpersonationBanner";
import AccountPreferences from "./pages/AccountPreferences";
import AdminLogin from "./pages/AdminLogin";
import ProtectedRoute from "./components/ProtectedRoute";
import AppLayout from "./components/AppLayout";
import CookieBanner from "./components/CookieBanner";
import { Toaster } from "./components/ui/sonner";
import { useAuth } from "./context/AuthContext";

function GlobalCookieBanner() {
  // App-wide policy: the cookie banner is always in Italian regardless of
  // user preference or browser locale (consistent with the Italian-flag
  // colour scheme — this site is for the Italian medical licensing exam).
  return <CookieBanner language="it" />;
}

function AppRouter() {
  const location = useLocation();
  // Detect session_id synchronously during render to prevent race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ImpersonationBanner />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout><Dashboard /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/training"
        element={
          <ProtectedRoute>
            <AppLayout><Training /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/review"
        element={
          <ProtectedRoute>
            <AppLayout><Review /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/exam"
        element={<Navigate to="/vip/oral" replace />}
      />
      <Route
        path="/account/preferences"
        element={
          <ProtectedRoute>
            <AppLayout><AccountPreferences /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ssm"
        element={
          <ProtectedRoute>
            <AppLayout><SSM /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vip"
        element={
          <ProtectedRoute>
            <AppLayout><VIP /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vip/lessons/:lessonId"
        element={
          <ProtectedRoute>
            <AppLayout><VIPLesson /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vip/exam"
        element={
          <ProtectedRoute>
            <AppLayout><VIPExam /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vip/exam/:attemptId"
        element={
          <ProtectedRoute>
            <AppLayout><VIPExamAttempt /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vip/oral"
        element={
          <ProtectedRoute>
            <AppLayout><VIPOralExam /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vip/oral/:attemptId"
        element={
          <ProtectedRoute>
            <AppLayout><VIPOralExam /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vip/oral-coach"
        element={
          <ProtectedRoute>
            <AppLayout><OralCoach /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/verify/:certId" element={<VerifyCertificate />} />
      <Route
        path="/mistakes"
        element={
          <ProtectedRoute>
            <AppLayout><Mistakes /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/favorites"
        element={
          <ProtectedRoute>
            <AppLayout><Favorites /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/premium"
        element={
          <ProtectedRoute>
            <AppLayout><Premium /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/aims"
        element={
          <ProtectedRoute>
            <AppLayout><AIMSPack /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout><Admin /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/vip"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout><VIPAdmin /></AppLayout>
          </ProtectedRoute>
        }
      >
        <Route index element={<VIPAdminOverview />} />
        <Route path="lessons" element={<VIPAdminContent />} />
        <Route path="lessons/:lessonId" element={<VIPAdminLessonBuilder />} />
        <Route path="costs" element={<VIPAdminCosts />} />
        <Route path="users" element={<VIPAdminUsers />} />
        <Route path="subscribers" element={<VIPAdminSubscribers />} />
        <Route path="devices" element={<VIPAdminDevices />} />
        <Route path="users/:userId" element={<VIPAdminUserDetail />} />
        <Route path="funnel" element={<VIPAdminFunnel />} />
        <Route path="migrate" element={<VIPAdminMigrate />} />
        <Route path="videos" element={<VIPAdminVideos />} />
        <Route path="payments" element={<VIPAdminPayments />} />
        <Route path="simulator" element={<VIPAdminSimulator />} />
        <Route path="security-reviews" element={<VIPAdminSecurityReviews />} />
      </Route>
      <Route path="/admin-login" element={<AdminLogin />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <GlobalCookieBanner />
          <Toaster theme="dark" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
